import { InterstitialKind } from "./types";

/**
 * Vercel intercepts cross-origin browser calls with HTML "interstitial" pages
 * in four observed cases that look like a normal API response to fetch():
 *
 *   • DDoS Mitigation / Attack Challenge → `200 OK  + text/html` (Linear #2568)
 *   • Deployment Protection (preview/QA) → `401     + text/html`
 *   • Firewall Deny (ai_bots, geo/IP)    → `403     + text/html`
 *   • Bot Protection / Rate-limit chall. → `429     + text/html`
 *
 * All four ruin JSON parsing and previously caused silent cart loss. Detect
 * them so callers can transparently retry through the same-origin
 * `/api/v1/*` proxy, which `vercel.json` `routes` augments with the
 * deployment-protection bypass header before forwarding to BACKEND_API_ORIGIN.
 *
 * Safety against false positives: our upstream API returns 4xx with
 * `application/json` (REST convention). HTML content-type on a 4xx is a
 * near-certain signal that Vercel's edge intercepted the request before it
 * reached the application — not a legitimate backend response.
 *
 * On 403 / 429 retry — design decision:
 * Vercel Firewall and Bot Protection are PROJECT-SCOPED. Same-origin proxy
 * retry traverses the WWW project's edge (still subject to its Firewall),
 * NOT the API project's edge. We rely on the assumption that frontend
 * traffic from real browsers is legitimate (no `ai_bots` matches, organic
 * UA strings), so 403/429 from the API project are almost always
 * false-positive interception of legit users rather than deliberate security
 * decisions. The bypass header (`x-vercel-protection-bypass`) covers ONLY
 * Deployment Protection — it does NOT bypass Firewall rules. Firewall
 * config MUST be kept at parity between WWW and API projects in the Vercel
 * dashboard to prevent configuration drift from creating de-facto bypass.
 */
export function detectVercelInterstitial(response: Response): InterstitialKind | null {
    // Normalize the content-type the same way `wrapperFetchJsonResponse` does:
    // strip parameters (`; charset=...`), trim, lowercase. Header values are
    // case-insensitive per RFC 7230 §3.2, and exact media-type equality avoids
    // false-matches on hypothetical strings like `text/html-bogus`.
    const rawContentType = response.headers.get("content-type") ?? "";
    const mediaType = (rawContentType.split(";")[0] ?? "").trim().toLowerCase();
    const isHtml = mediaType === "text/html";
    if (!isHtml) return null;
    if (response.status === 200) return "ddos-challenge";
    if (response.status === 401) return "deployment-protection";
    if (response.status === 403) return "firewall-deny";
    if (response.status === 429) return "rate-limit-challenge";
    return null;
}
